/**
 * Video utilities for handling YouTube and regular video playback
 */

/**
 * Checks if a URL is a YouTube video URL
 * @param {string} url - The URL to check
 * @returns {boolean} True if the URL is a YouTube URL
 */
export const isYouTubeUrl = (url) => {
  if (!url) return false;
  return url.includes('youtube.com') || url.includes('youtu.be');
};

/**
 * Extracts YouTube video ID from various URL formats
 * Supports: regular videos, shorts, embedded videos, and short links
 * @param {string} url - The YouTube URL
 * @returns {string|null} The video ID or null if not found
 */
export const getYouTubeId = (url) => {
  if (!url) return null;

  // Check for YouTube Shorts format first
  const shortsMatch = url.match(/youtube\.com\/shorts\/([^"&?/\s]{11})/);
  if (shortsMatch) return shortsMatch[1];

  // Regular YouTube URL patterns
  const match = url.match(
    /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/
  );
  return match ? match[1] : null;
};

/**
 * Generates a YouTube thumbnail URL from a video URL
 * @param {string} url - The YouTube URL
 * @returns {string|null} The thumbnail URL or null if no video ID found
 */
export const getYouTubeThumbnail = (url) => {
  const videoId = getYouTubeId(url);
  return videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null;
};

/**
 * Plays a video element with error handling
 * @param {HTMLVideoElement} videoRef - The video element ref
 */
export const playVideo = (videoRef) => {
  if (videoRef && typeof videoRef.play === 'function') {
    videoRef.play().catch((err) => {
      console.log('Autoplay prevented:', err);
    });
  }
};

/**
 * Pauses a video element and resets it to the beginning
 * @param {HTMLVideoElement} videoRef - The video element ref
 */
export const pauseAndResetVideo = (videoRef) => {
  if (videoRef) {
    videoRef.pause();
    videoRef.currentTime = 0;
  }
};

/**
 * Stops all videos in a collection and resets them
 * @param {Object} videoRefs - Object or array of video element refs
 */
export const stopAllVideos = (videoRefs) => {
  if (!videoRefs) return;

  const refs = Array.isArray(videoRefs) ? videoRefs : Object.values(videoRefs);

  refs.forEach((videoRef) => {
    if (videoRef) {
      pauseAndResetVideo(videoRef);
    }
  });
};

/**
 * Generates YouTube iframe URL with custom parameters
 * @param {string} url - The YouTube URL
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoplay - Enable autoplay (default: true)
 * @param {boolean} options.mute - Mute the video (default: false)
 * @param {boolean} options.loop - Loop the video (default: false)
 * @param {boolean} options.controls - Show controls (default: true)
 * @param {boolean} options.modestbranding - Hide YouTube branding (default: false)
 * @param {boolean} options.rel - Show related videos (default: false)
 * @returns {string|null} The iframe URL or null if invalid
 */
export const getYouTubeEmbedUrl = (url, options = {}) => {
  const videoId = getYouTubeId(url);
  if (!videoId) return null;

  const {
    autoplay = true,
    mute = false,
    loop = false,
    controls = true,
    modestbranding = false,
    rel = false,
  } = options;

  const params = new URLSearchParams();
  if (autoplay) params.append('autoplay', '1');
  if (mute) params.append('mute', '1');
  if (loop) {
    params.append('loop', '1');
    params.append('playlist', videoId);
  }
  if (controls) params.append('controls', '1');
  else params.append('controls', '0');
  if (modestbranding) params.append('modestbranding', '1');
  if (!rel) params.append('rel', '0');

  return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
};

/**
 * Checks if a video URL is valid (not empty and has proper format)
 * @param {string} url - The video URL to validate
 * @returns {boolean} True if the URL appears valid
 */
export const isValidVideoUrl = (url) => {
  if (!url || typeof url !== 'string') return false;

  // Basic URL validation
  try {
    new URL(url);
    return true;
  } catch {
    // Could be a relative path, which might still be valid
    return url.trim().length > 0;
  }
};
